import { useEffect, useMemo, useState } from "react"
import { Head, Deferred } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Card } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Skeleton } from "@/components/ui/skeleton"
import { Separator } from "@/components/ui/separator"
import { ProjectMap } from "@/components/projects/project-map"
import { ProjectSummary } from "@/components/projects/project-summary"
import { NotesTab } from "@/components/projects/notes-tab"
import { EventsTab } from "@/components/projects/events-tab"
import { TasksTab } from "@/components/projects/tasks-tab"
import { DealsTab } from "@/components/projects/deals-tab"
import { OwnersTab } from "@/components/projects/owners-tab"
import { ChecklistTab } from "@/components/projects/checklist-tab"
import { HistoryTab } from "@/components/projects/history-tab"
import { PotentielTab } from "@/components/projects/potentiel-tab"
import { BuildingDetails } from "@/components/explore/details/building-details"
import { ProjectNavigation } from "@/components/projects/project-navigation"
import { useProjectParcels } from "@/hooks/use-project-parcels"
import { computePotentiel } from "@/lib/potentiel-utils"
import {
  LayoutDashboard,
  StickyNote,
  CalendarClock,
  ListChecks,
  Coins,
  Users,
  ClipboardCheck,
  Calendar,
  ScrollText,
  TrendingUp,
  Building2,
} from "lucide-react"
import { geoFetch } from "@/lib/explore/api/geo-fetch"
import type {
  Project,
  ProjectNote,
  ProjectEvent,
  ProjectTask,
  Deal,
  Owner,
  ChecklistItem,
  UserSummary,
  ProjectTag,
  DomainEvent,
  PotentielData,
  PipeStatusRecord,
} from "@/types"

import { STATUS_BASE_URLS } from "@/lib/project-configs"

type Props = {
  project: Project
  notes: ProjectNote[]
  events: ProjectEvent[]
  tasks: ProjectTask[]
  deals: Deal[]
  owners: Owner[]
  checklist_items: ChecklistItem[]
  users: UserSummary[]
  teams: { id: string; name: string }[]
  project_tags: ProjectTag[]
  domain_events: DomainEvent[]
  potentiel: PotentielData | null
  pipe_statuses: PipeStatusRecord[]
  partner_organisations: { id: string; name: string }[]
  mapbox_token: string
  google_maps_key: string
  base_url: string
  nav_key: string | null
  prev_project_id: string | null
  next_project_id: string | null
}


function ProjectShow({
  project,
  notes,
  events,
  tasks,
  deals,
  owners,
  checklist_items,
  users,
  teams,
  project_tags,
  domain_events,
  potentiel,
  pipe_statuses,
  partner_organisations,
  mapbox_token,
  google_maps_key,
  base_url,
  nav_key,
  prev_project_id,
  next_project_id,
}: Props) {
  const { fetchedParcels, parcelGeojson, loading: parcelsLoading } = useProjectParcels(project.parcels, project.city_code ?? undefined)

  const allBuildings = useMemo(
    () => fetchedParcels.flatMap((p) => p.buildings ?? []),
    [fetchedParcels],
  )

  const allCorporationOwners = useMemo(
    () => fetchedParcels.filter((p) => p.corporationOwners && p.corporationOwners.length > 0),
    [fetchedParcels],
  )

  const constructionYear = useMemo(() => {
    const years = allBuildings.map((b) => b.annee_construction).filter(Boolean) as string[]
    return years.length > 0 ? years.sort()[0] : null
  }, [allBuildings])

  const sdpIncreasePct = useMemo(() => {
    if (!potentiel) return null
    return computePotentiel(potentiel).sdpIncreasePct
  }, [potentiel])

  const firstParcelIdu = fetchedParcels[0]?.properties?.idu ?? null

  const [pluDoc, setPluDoc] = useState<{ gpu_doc_id: string; nomfic: string } | null>(null)
  useEffect(() => {
    if (!project.longitude || !project.latitude) return
    const url = `https://www.geoportail-urbanisme.gouv.fr/api/feature-info/du?lon=${project.longitude}&lat=${project.latitude}&type=du&typeName=zone_urba`
    geoFetch(url)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        const features = json?.features || json
        const feature = Array.isArray(features) ? features[0] : features
        const props = feature?.properties || feature
        if (props?.gpu_doc_id && props?.nomfic) {
          setPluDoc({ gpu_doc_id: props.gpu_doc_id, nomfic: props.nomfic })
        }
      })
      .catch(() => {})
  }, [project.longitude, project.latitude])

  return (
    <>
      <Head title={project.name} />
      <PageHeader
        title={project.name}
        back={STATUS_BASE_URLS[project.status] || "/projects"}
        extra={nav_key ? <ProjectNavigation prevId={prev_project_id} nextId={next_project_id} navKey={nav_key} baseUrl={base_url} /> : undefined}
      />
      <Card className="grid gap-0 overflow-hidden md:grid-cols-2">
        <ProjectMap
          project={project}
          mapboxToken={mapbox_token}
          googleMapsKey={google_maps_key}
          fetchedParcels={fetchedParcels}
          parcelGeojson={parcelGeojson}
        />
        <ProjectSummary project={project} users={users} teams={teams} project_tags={project_tags} pipeStatuses={pipe_statuses} partnerOrganisations={partner_organisations} constructionYear={constructionYear} corporationOwnerParcels={allCorporationOwners} pluZone={potentiel?.plu_zone ?? null} firstParcelIdu={firstParcelIdu} pluDoc={pluDoc}>
          <Tabs defaultValue="overview">
            <TabsList variant="line" className="w-full justify-between">
              <TabsTrigger value="overview" title="Aperçu">
                <LayoutDashboard className="size-5" />
              </TabsTrigger>
              <TabsTrigger value="notes" title="Notes">
                <StickyNote className="size-5" /> {notes?.length ?? "—"}
              </TabsTrigger>
              <TabsTrigger value="events" title="Événements">
                <CalendarClock className="size-5" /> {events?.length ?? "—"}
              </TabsTrigger>
              <TabsTrigger value="tasks" title="Tâches">
                <ListChecks className="size-5" /> {tasks?.length ?? "—"}
              </TabsTrigger>
              <TabsTrigger value="deals" title="Offres">
                <Coins className="size-5" /> {deals?.length ?? "—"}
              </TabsTrigger>
              <TabsTrigger value="owners" title="Propriétaires">
                <Users className="size-5" /> {owners?.length ?? "—"}
              </TabsTrigger>
              <TabsTrigger value="checklist" title="Checklist">
                <ClipboardCheck className="size-5" /> {checklist_items?.length ?? "—"}
              </TabsTrigger>
              <TabsTrigger value="potentiel" title="Potentiel">
                <TrendingUp className="size-5" />
                {sdpIncreasePct != null && (
                  <span className={sdpIncreasePct > 0 ? "text-emerald-600" : "text-amber-600"}>
                    {sdpIncreasePct > 0 ? "+" : ""}{sdpIncreasePct}%
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="history" title="Historique">
                <ScrollText className="size-5" /> {domain_events?.length ?? "—"}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              {project.first_owner && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Premier propriétaire</p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="font-medium">{project.first_owner.display_name}</span>
                      {project.first_owner.phone_number && (
                        <span className="text-muted-foreground">{project.first_owner.phone_number}</span>
                      )}
                      {project.first_owner.email && (
                        <span className="text-muted-foreground">{project.first_owner.email}</span>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Building details from parcel data */}
              {(parcelsLoading || allBuildings.length > 0) && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                      <Building2 className="size-3.5" />
                      Bâtiment
                    </p>
                    {parcelsLoading ? (
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-2/3" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                    ) : (
                      <BuildingDetails buildingData={allBuildings} />
                    )}
                  </div>
                </>
              )}

              <Separator />
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                {project.created_by && (
                  <span>
                    Créé par {project.created_by.first_name} {project.created_by.last_name}
                    {project.created_at && (
                      <>
                        {" "}
                        le{" "}
                        {new Date(project.created_at).toLocaleDateString("fr-FR", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </>
                    )}
                  </span>
                )}
                {!project.created_by && project.created_at && (
                  <span>
                    <Calendar className="mr-1 inline size-3" />
                    Créé le{" "}
                    {new Date(project.created_at).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                )}
                {project.updated_at && project.updated_at !== project.created_at && (
                  <span>
                    Modifié le{" "}
                    {new Date(project.updated_at).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                )}
              </div>
            </TabsContent>

            <TabsContent value="notes" forceMount className="data-[state=inactive]:hidden">
              <Deferred data="notes" fallback={null}>
                <NotesTab projectId={project.id} notes={notes} />
              </Deferred>
            </TabsContent>

            <TabsContent value="events" forceMount className="data-[state=inactive]:hidden">
              <Deferred data="events" fallback={null}>
                <EventsTab projectId={project.id} events={events} />
              </Deferred>
            </TabsContent>

            <TabsContent value="tasks" forceMount className="data-[state=inactive]:hidden">
              <Deferred data="tasks" fallback={null}>
                <TasksTab projectId={project.id} tasks={tasks} users={users} />
              </Deferred>
            </TabsContent>

            <TabsContent value="deals" forceMount className="data-[state=inactive]:hidden">
              <Deferred data="deals" fallback={null}>
                <DealsTab projectId={project.id} deals={deals} />
              </Deferred>
            </TabsContent>

            <TabsContent value="owners" forceMount className="data-[state=inactive]:hidden">
              <Deferred data="owners" fallback={null}>
                <OwnersTab projectId={project.id} owners={owners} />
              </Deferred>
            </TabsContent>

            <TabsContent value="checklist" forceMount className="data-[state=inactive]:hidden">
              <Deferred data="checklist_items" fallback={null}>
                <ChecklistTab projectId={project.id} checklistItems={checklist_items} />
              </Deferred>
            </TabsContent>

            <TabsContent value="potentiel" forceMount className="data-[state=inactive]:hidden">
              <Deferred data="potentiel" fallback={null}>
                <PotentielTab data={potentiel} latitude={project.latitude} longitude={project.longitude} />
              </Deferred>
            </TabsContent>

            <TabsContent value="history" forceMount className="data-[state=inactive]:hidden">
              <Deferred data="domain_events" fallback={null}>
                <HistoryTab domainEvents={domain_events} users={users} pipeStatuses={pipe_statuses} />
              </Deferred>
            </TabsContent>
          </Tabs>
        </ProjectSummary>
      </Card>
    </>
  )
}

ProjectShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default ProjectShow
